/* GRADA Corporate Gift Box Builder — gift-builder.js v2 (ES2020, no build step)

   Two steps: Build (box + quantity + extras, one screen) then Send
   (delivery + contact). One config object holds every number (mirroring
   docs/gift-builder-pricing.json via the section settings), one state object
   holds every answer, and derive() is a pure function from state to
   everything on screen. All prices inc GST.

   Delivery to individual addresses is an ESTIMATE — it is never added to the
   total or charged at order time. Logo artwork is collected after the order.
*/

(function () {
  'use strict';

  var root = document.querySelector('[data-gift-builder]');
  if (!root) return;

  var configEl = root.querySelector('[data-gb-config]');
  if (!configEl) return;

  var CFG;
  try {
    CFG = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  // Price settings are text fields in the theme editor, so coerce everything
  // that must be a number — a stray character must not turn the maths NaN.
  function num(v, fallback) {
    var n = parseFloat(v);
    return isFinite(n) ? n : fallback;
  }
  ['so', 'decaf', 'deluxe'].forEach(function (id) {
    CFG.boxes[id].corp = num(CFG.boxes[id].corp, 0);
    CFG.boxes[id].rrp = num(CFG.boxes[id].rrp, 0);
  });
  Object.keys(CFG.addons).forEach(function (k) { CFG.addons[k] = num(CFG.addons[k], 0); });
  // The sleeve is priced on its own break, so it is not in CFG.addons.
  CFG.sleeve = CFG.sleeve || {};
  CFG.sleeve.under = num(CFG.sleeve.under, 10);
  CFG.sleeve.at = num(CFG.sleeve.at, 5);
  CFG.sleeve.breakAt = Math.max(1, Math.round(num(CFG.sleeve.breakAt, 50)));
  CFG.glassesEx = num(CFG.glassesEx, 0);
  CFG.individualEstFrom = num(CFG.individualEstFrom, 0);
  CFG.depositPct = num(CFG.depositPct, 50);
  CFG.depositDays = num(CFG.depositDays, 5);
  // The deposit secures the order for 48 hours; the balance is invoiced on
  // one date for every order (Roscoe, 6 Oct 2026). Both are section settings.
  CFG.depositHours = num(CFG.depositHours, 48);
  CFG.finalInvoice = CFG.finalInvoice || '16 November';
  // Laurie raises the order and emails the deposit invoice whatever `intent`
  // says: its order route accepts intent and changes nothing (17 Oct 2026
  // audit). So "Email me a quote" promised "nothing is booked" and sent a 50%
  // deposit invoice. Until Laurie raises a quote with no deposit and no
  // invoice, laurie mode offers the order alone and always sends 'order'.
  // The contact-form mode keeps both: its email says QUOTE ONLY.
  var QUOTE_OFFERED = CFG.checkoutMode !== 'laurie';

  // The mockup plate. A broken JSON string in the theme editor turns the
  // mockup off; it must never take the rest of the builder with it.
  var PLATE = null;
  try {
    PLATE = JSON.parse(CFG.plateJson);
    if (!PLATE.panel || !PLATE.size || !PLATE.gold) PLATE = null;
  } catch (e) { PLATE = null; }
  var MOCKUP_ON = !!(PLATE && CFG.plateImage);
  var LOGO_MAX_BYTES = 8 * 1024 * 1024;
  var LOGO_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/webp'];
  // Guard-rail only: high enough never to block a real corporate order, low
  // enough that a fat-fingered "999999999" cannot render a $127bn total.
  var MAX_QTY = 10000;

  var STORAGE_KEY = 'grada_gift_builder_state';
  var KEY_STORAGE = 'grada_gift_builder_key';
  var PERSIST_KEY = 'grada_gift_builder_box_v2';

  /* ============================================================
     ACCESS GATE — hides the page behind ?key=<value>. Not security.
     ============================================================ */

  function gateAllows() {
    var required = (CFG.accessKey || '').trim();
    if (!required) return true;

    var supplied = new URLSearchParams(location.search).get('key');
    if (supplied === required) {
      // Shopify's contact form redirects to ?contact_posted=true and drops the
      // rest of the query, so remember the key for the rest of the session.
      try { sessionStorage.setItem(KEY_STORAGE, required); } catch (e) {}
      return true;
    }

    try { return sessionStorage.getItem(KEY_STORAGE) === required; } catch (e) { return false; }
  }

  if (!gateAllows()) {
    var locked = document.querySelector('[data-gb-locked]');
    if (locked) locked.hidden = false;
    root.remove();
    return;
  }

  /* ============================================================
     STATIC COPY
     ============================================================ */

  /* WHAT CAN CHANGE WITHOUT TOUCHING LAURIE (5 Oct 2026)
     Laurie prices every order itself from ids and flags; the page never sends
     a price (docs/Gift-Builder-Laurie-Spec-2026-09-11.md). So:
     - Free to change, page only: photos, layout, CSS, arrows and motion, and
       display copy: eyebrow, title, cardSub, sub, brief, contents, labels.
     - Laurie first, page second (or orders misprice or fail): the box ids
       so / decaf / deluxe, the addon keys sleeve / beansSO / beansDecaf / card,
       delivery central / individual, intent, any field name in the order
       body, the text/plain content type, the body.ok reply, the logo upload
       fields, and every price, tier, sleeve break, MOQ and the glasses price
       (Laurie reproduces derive() to the cent).
     - Change with care, not Laurie but read downstream: `name` (Klaviyo's
       Gift Box Order "Box", the cart line, the team email), the Klaviyo
       metric and property names (the confirmation flow reads them), and the
       contact form's hidden field keys (the team email). */
  var BOXES = [
    // Each box lists what is in it on its own card (Roscoe, 3 Sep: "show
    // what's included in each box rather than up here"). `brief` is the
    // one-line form the compact rows use; `contents` is the list on the
    // selected card. The selected card sets the name as a small `eyebrow`
    // over a display `title`, with `cardSub` under it (Roscoe, 5 Oct: the
    // name over two lines, the price at the bottom). `zoom` crops a product
    // shot with a lot of backdrop tighter on a phone, where the photo is
    // square. `sleeve` is a second photo, the outside of the box, behind an
    // arrow on the photo, and `back` a third, the back of the box, after it.
    // `glassesImg` is the photo with the Nick & Nora pair,
    // shown once that upgrade is ticked. `theme` dresses the card in its own
    // bottle's colours (Single Origin blue, Decaf terracotta, Deluxe black).
    // `tagline` is the line printed on that box's sleeve, caps then script.
    // `name` is still what the order, the
    // summary and the rows use.
    { id: 'so', img: 'so', name: 'Gift Box — Single Origin', sub: '7 serves',
      eyebrow: 'Gift Box', title: 'Single Origin', cardSub: '7 serves · 700ml bottle', zoom: '122%',
      sleeve: 'soSleeve', back: 'soBack', glassesImg: 'soGlasses', theme: 'so', tagline: ['Made for', 'after'],
      brief: 'Bottle · Coupe · Bar Shaker · Recipe Card · Sleeve',
      contents: ['GRADA 01 Single Origin', 'Japanese Hand-Blown Coupe', 'Polished Bar Shaker', 'Recipe Card', 'Printed Sleeve'] },
    { id: 'decaf', img: 'decaf', name: 'Gift Box — Midnight Decaf', sub: '7 serves · 99.9% Caffeine Free',
      eyebrow: 'Gift Box', title: 'Midnight Decaf', cardSub: '7 serves · 700ml bottle · 99.9% Caffeine Free', zoom: '122%',
      sleeve: 'decafSleeve', back: 'decafBack', glassesImg: 'decafGlasses', theme: 'decaf', tagline: ['Made without', 'Compromise'],
      brief: 'Bottle · Coupe · Bar Shaker · Recipe Card · Sleeve',
      contents: ['GRADA 02 Midnight Decaf', 'Japanese Hand-Blown Coupe', 'Polished Bar Shaker', 'Recipe Card', 'Printed Sleeve'] },
    { id: 'deluxe', img: 'deluxe', name: 'Deluxe Box', sub: '14 serves · both bottles',
      eyebrow: 'Gift Box', title: 'Deluxe', cardSub: '14 serves · two 700ml bottles', theme: 'deluxe', sleeve: 'deluxeSleeve', back: 'deluxeBack',
      tagline: ['Made for', 'Celebrating'],
      brief: 'Two Bottles · Two Nick & Nora Glasses · Bar Shaker · Recipe Card · Sleeve',
      contents: ['GRADA 01 Single Origin', 'GRADA 02 Midnight Decaf', 'Two Nick & Nora Glasses', 'Polished Bar Shaker', 'Recipe Card', 'Printed Sleeve'] }
  ];

  var ADDONS = [
    { id: 'sleeve', name: 'Your logo on every sleeve' },
    // `zoom` is the full photo the thumbnail opens in the viewer.
    { id: 'beansSO', img: 'beansSO', zoom: 'beansSOFull', name: '250g Single Origin Coffee Beans' },
    { id: 'beansDecaf', img: 'beansDecaf', zoom: 'beansDecafFull', name: '250g Midnight Decaf Coffee Beans' },
    { id: 'card', img: 'note', zoom: 'noteFull', name: 'A handwritten note' }
  ];

  // Australian mobiles, normalised to E.164 for the notification. Returns null
  // when the number is not a recognisable AU mobile — the caller still sends
  // what was typed rather than dropping it, since an overseas or landline
  // number on a real order is worth more than a clean format.
  function auMobile(raw) {
    var d = String(raw || '').replace(/[^\d+]/g, '');
    if (/^04\d{8}$/.test(d)) return '+61' + d.slice(1);
    if (/^\+614\d{8}$/.test(d)) return d;
    if (/^614\d{8}$/.test(d)) return '+' + d;
    return null;
  }

  // One reference for the whole order, so the confirmation screen, the Klaviyo
  // email and the Shopify draft order all quote the same string back to the
  // customer. Date-and-time based rather than random so it sorts.
  function makeRef() {
    var n = new Date();
    var p2 = function (x) { return (x < 10 ? '0' : '') + x; };
    return 'GB-' + String(n.getFullYear()).slice(2) + p2(n.getMonth() + 1) + p2(n.getDate())
      + '-' + p2(n.getHours()) + p2(n.getMinutes());
  }

  var ADDON_SHORT = {
    sleeve: 'logo sleeve', beansSO: '250g SO beans', beansDecaf: '250g decaf beans', card: 'handwritten note'
  };

  /* ============================================================
     STATE
     ============================================================ */

  function freshState() {
    // The box arrives fully configured — sensible defaults everywhere, so the
    // customer edits a finished order. Nothing blocks the path to ordering.
    return {
      view: 'intro',
      box: 'so',
      glasses: false,
      qty: CFG.moq,
      addons: { sleeve: false, beansSO: false, beansDecaf: false, card: false },
      delivery: 'central',
      // 'order' raises an invoice with a deposit; 'quote' is the soft path.
      intent: 'order',
      // The uploaded logo: what it was called, the mockup we drew from it, and
      // anything that went wrong. The file itself lives in logoFile, outside
      // state, because state is serialised to localStorage and a File is not.
      logo: null,
      logoPreview: '',
      examplePreview: '',
      logoErr: '',
      logoBusy: false
    };
  }

  var state = freshState();
  var logoFile = null;

  /* ============================================================
     PERSISTENCE — the configured box survives leaving the page
     ============================================================ */

  function savePersisted() {
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({
        box: state.box, glasses: state.glasses, qty: state.qty,
        addons: state.addons, delivery: state.delivery, intent: state.intent
      }));
    } catch (e) {}
  }

  function restorePersisted() {
    var saved;
    try { saved = JSON.parse(localStorage.getItem(PERSIST_KEY)); } catch (e) { return; }
    if (!saved || !CFG.boxes[saved.box]) return;

    state.box = saved.box;
    state.glasses = saved.glasses === true && saved.box !== 'deluxe';
    if (typeof saved.qty === 'number' && saved.qty >= 1) state.qty = Math.round(saved.qty);
    if (saved.addons && typeof saved.addons === 'object') {
      Object.keys(state.addons).forEach(function (k) { state.addons[k] = saved.addons[k] === true; });
    }
    if (saved.delivery === 'central' || saved.delivery === 'individual') state.delivery = saved.delivery;
    if (saved.intent === 'order' || saved.intent === 'quote') state.intent = saved.intent;
  }

  function clearPersisted() {
    try { localStorage.removeItem(PERSIST_KEY); } catch (e) {}
  }

  /* ============================================================
     HELPERS
     ============================================================ */

  // Money is only ever exchanged in whole cents; keep every derived figure on
  // that grid so nothing on screen disagrees with anything else.
  function cents(n) {
    return Math.round(n * 100) / 100;
  }

  // The branded sleeve is a short-run digital gold print: its unit cost
  // genuinely falls with quantity, which is why it has a break of its own AND
  // why the volume tier must never cut it as well — that would discount the
  // one line whose cost already moved, twice.
  function sleevePrice(qty) {
    return qty >= CFG.sleeve.breakAt ? CFG.sleeve.at : CFG.sleeve.under;
  }

  /* ============================================================
     LOGO MOCKUP — their logo, in gold, on the box, in their browser

     Ported from box-mockup/lib/canvas-render.js in the 01 project, which is
     diffed against the server renderer there (placement identical, mean
     channel difference 0.003). Keep the two in step or the preview and the
     proof the team sends will disagree.
     ============================================================ */

  var plateImgPromise = null;
  var mockupCanvas = null;           // full size, kept for the download
  var mockupObjectUrl = null;

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('read')); };
      img.src = src;
    });
  }

  function platePromise() {
    if (!plateImgPromise) plateImgPromise = loadImage(CFG.plateImage);
    return plateImgPromise;
  }

  // Everything more opaque than this is the logo. An explicit box, because the
  // server side has to reach the same one to the pixel.
  function alphaBox(img, threshold) {
    var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0);
    var data = x.getImageData(0, 0, w, h).data;
    var top = h, left = w, right = -1, bottom = -1;
    for (var y = 0; y < h; y++) {
      for (var i = (y * w) << 2, px = 0; px < w; px++, i += 4) {
        if (data[i + 3] > threshold) {
          if (px < left) left = px;
          if (px > right) right = px;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }
    if (right < 0) return { left: 0, top: 0, width: w, height: h };
    return { left: left, top: top, width: right - left + 1, height: bottom - top + 1 };
  }

  // Halve, then halve again, then finish. One big draw of a 1594px plate into
  // a 540px box drops the gold hairline rule on the sleeve.
  function downscale(source, targetW) {
    var sw = source.naturalWidth || source.width;
    var sh = source.naturalHeight || source.height;
    var targetH = Math.round((sh / sw) * targetW);
    var src = source;
    while (sw >= targetW * 2 && sh >= targetH * 2) {
      var half = document.createElement('canvas');
      half.width = Math.max(targetW, Math.round(sw / 2));
      half.height = Math.max(targetH, Math.round(sh / 2));
      var hx = half.getContext('2d');
      hx.imageSmoothingEnabled = true; hx.imageSmoothingQuality = 'high';
      hx.drawImage(src, 0, 0, sw, sh, 0, 0, half.width, half.height);
      src = half; sw = half.width; sh = half.height;
    }
    var out = document.createElement('canvas');
    out.width = targetW; out.height = targetH;
    var ox = out.getContext('2d');
    ox.imageSmoothingEnabled = true; ox.imageSmoothingQuality = 'high';
    ox.drawImage(src, 0, 0, sw, sh, 0, 0, targetW, targetH);
    return out;
  }

  // The foil: the ramp sampled off the real gold, plus the diagonal sheen.
  // A flat gold fill reads as a sticker.
  function paintFoil(ctx, w, h, stops) {
    var g = ctx.createLinearGradient(0, 0, 0, h);
    stops.forEach(function (st) { g.addColorStop(st.at, 'rgb(' + st.rgb.join(',') + ')'); });
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    var sheen = ctx.createLinearGradient(0, 0, w, h * 0.35);
    sheen.addColorStop(0, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.46, 'rgba(255,255,255,0.34)');
    sheen.addColorStop(0.62, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen; ctx.fillRect(0, 0, w, h);
  }

  function composeMockup(plateImg, logoImg) {
    var box = alphaBox(logoImg, 12);
    var maxW = Math.round(PLATE.panel.w * PLATE.maxWidthRatio);
    var maxH = Math.round(PLATE.panel.h * PLATE.maxHeightRatio);
    var scale = Math.min(maxW / box.width, maxH / box.height);
    var lw = Math.max(1, Math.round(box.width * scale));
    var lh = Math.max(1, Math.round(box.height * scale));

    var layer = document.createElement('canvas');
    layer.width = lw; layer.height = lh;
    var lx = layer.getContext('2d');
    lx.imageSmoothingEnabled = true; lx.imageSmoothingQuality = 'high';
    var stepSrc = logoImg, sx = box.left, sy = box.top, sw = box.width, sh = box.height;
    while (sw >= lw * 2 && sh >= lh * 2) {
      var half = document.createElement('canvas');
      half.width = Math.max(lw, Math.round(sw / 2));
      half.height = Math.max(lh, Math.round(sh / 2));
      var hx = half.getContext('2d');
      hx.imageSmoothingEnabled = true; hx.imageSmoothingQuality = 'high';
      hx.drawImage(stepSrc, sx, sy, sw, sh, 0, 0, half.width, half.height);
      stepSrc = half; sx = 0; sy = 0; sw = half.width; sh = half.height;
    }
    lx.drawImage(stepSrc, sx, sy, sw, sh, 0, 0, lw, lh);

    var gold = document.createElement('canvas');
    gold.width = lw; gold.height = lh;
    var gx = gold.getContext('2d');
    paintFoil(gx, lw, lh, PLATE.gold.stops);
    gx.globalCompositeOperation = 'destination-in';
    gx.drawImage(layer, 0, 0);

    var out = document.createElement('canvas');
    out.width = PLATE.size.w; out.height = PLATE.size.h;
    var ox = out.getContext('2d');
    ox.drawImage(plateImg, 0, 0, PLATE.size.w, PLATE.size.h);
    ox.drawImage(gold,
      Math.round(PLATE.panel.x + (PLATE.panel.w - lw) / 2 + ((PLATE.optical && PLATE.optical.dx) || 0)),
      Math.round(PLATE.panel.y + (PLATE.panel.h - lh) / 2 + ((PLATE.optical && PLATE.optical.dy) || 0)));
    return out;
  }

  /**
   * The example sleeve: the same box, with a neutral gold placeholder where
   * their mark will go. Deliberately NOT a real client's logo. The previous
   * example carried EG Funds' mark and read as somebody else's box.
   */
  var examplePromise = null;
  function exampleSleeve() {
    if (examplePromise) return examplePromise;
    examplePromise = platePromise().then(function (plateImg) {
      var label = document.createElement('canvas');
      var lw = Math.round(PLATE.panel.w * PLATE.maxWidthRatio);
      var lh = Math.round(PLATE.panel.h * PLATE.maxHeightRatio);
      label.width = lw; label.height = lh;
      var lx = label.getContext('2d');
      lx.textAlign = 'center';
      lx.textBaseline = 'middle';
      lx.fillStyle = '#fff';
      lx.font = '600 ' + Math.round(lh * 0.28) + 'px Georgia, serif';
      lx.fillText('YOUR LOGO', lw / 2, lh * 0.40);
      lx.font = '600 ' + Math.round(lh * 0.13) + 'px Georgia, serif';
      lx.fillText('IN GOLD, HERE', lw / 2, lh * 0.68);

      var gold = document.createElement('canvas');
      gold.width = lw; gold.height = lh;
      var gx = gold.getContext('2d');
      paintFoil(gx, lw, lh, PLATE.gold.stops);
      gx.globalCompositeOperation = 'destination-in';
      gx.drawImage(label, 0, 0);

      var out = document.createElement('canvas');
      out.width = PLATE.size.w; out.height = PLATE.size.h;
      var ox = out.getContext('2d');
      ox.drawImage(plateImg, 0, 0, PLATE.size.w, PLATE.size.h);
      ox.drawImage(gold,
        Math.round(PLATE.panel.x + (PLATE.panel.w - lw) / 2 + ((PLATE.optical && PLATE.optical.dx) || 0)),
        Math.round(PLATE.panel.y + (PLATE.panel.h - lh) / 2 + ((PLATE.optical && PLATE.optical.dy) || 0)));
      // A photograph with gold text on it: JPEG at this quality is a tenth of
      // the PNG and reads the same. The download keeps the full PNG canvas.
      return downscale(out, 630).toDataURL('image/jpeg', 0.86);
    }).catch(function () { return ''; });
    return examplePromise;
  }

  function logoError(msg) {
    state.logoErr = msg;
    state.logoBusy = false;
    render();
  }

  function acceptLogo(file) {
    if (!file || !MOCKUP_ON) return;
    if (file.size > LOGO_MAX_BYTES) return logoError('That file is over 8MB. A PNG or SVG of the logo is plenty.');
    if (file.type && LOGO_TYPES.indexOf(file.type) < 0) return logoError('Send a PNG, SVG or JPG. Print files like PDF or EPS come later, with the proof.');

    state.logoErr = '';
    state.logoBusy = true;
    render();

    var url = URL.createObjectURL(file);
    Promise.all([platePromise(), loadImage(url)])
      .then(function (pair) {
        mockupCanvas = composeMockup(pair[0], pair[1]);
        if (mockupObjectUrl) URL.revokeObjectURL(mockupObjectUrl);
        mockupObjectUrl = null;
        state.logo = { name: file.name, size: file.size, type: file.type || '' };
        state.logoPreview = downscale(mockupCanvas, 630).toDataURL('image/jpeg', 0.86);
        state.logoBusy = false;
        // The file itself only travels if an endpoint is configured; see
        // sendLogo(). Kept in memory either way so the buyer can download it.
        logoFile = file;
        render();
      })
      .catch(function () {
        logoError('That file could not be read as an image. A PNG with a transparent background works best.');
      })
      .then(function () { URL.revokeObjectURL(url); });
  }

  function downloadMockup() {
    if (!mockupCanvas) return;
    mockupCanvas.toBlob(function (blob) {
      var a = document.createElement('a');
      var href = URL.createObjectURL(blob);
      a.href = href;
      a.download = 'grada-corporate-box.png';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(href); }, 4000);
    }, 'image/png');
  }

  /**
   * Only when an endpoint is configured. Fire and forget on a short timeout:
   * an upload that hangs must never hold up an order, and the team's fallback
   * (ask for artwork after the order) is what happens today anyway.
   */
  function sendLogo(reference) {
    if (!CFG.logoUploadEndpoint || !logoFile) return Promise.resolve('');
    var body = new FormData();
    body.append('logo', logoFile);
    body.append('reference', reference || '');
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctrl) setTimeout(function () { ctrl.abort(); }, 8000);
    return fetch(CFG.logoUploadEndpoint, { method: 'POST', body: body, signal: ctrl && ctrl.signal })
      .then(function (r) { return r.json(); })
      .then(function (j) { return (j && j.url) || ''; })
      .catch(function () { return ''; });
  }

  function money(n) {
    var r = Math.round(n * 100) / 100;
    var opts = Number.isInteger(r)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return '$' + r.toLocaleString('en-AU', opts);
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * The theme header is fixed, so the builder reserves its height via
   * --gb-header. Measured rather than hardcoded — the header can reflow.
   */
  function syncHeaderOffset() {
    var header = document.querySelector('.grada-header');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) root.style.setProperty('--gb-header', h + 'px');
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function el(sel, scope) {
    return (scope || root).querySelector(sel);
  }

  /* ============================================================
     DERIVE — pure: state -> every number and label on screen

     unit = box + glasses upgrade + add-ons, all inc GST; the volume tier
     discounts the unit. Individual delivery is an estimate only and never
     joins the total.
     ============================================================ */

  function derive() {
    var s = state;
    var qty = s.qty;
    var box = CFG.boxes[s.box];
    var duo = s.box === 'deluxe';
    var glasses = s.glasses && !duo;

    var addonKeys = Object.keys(s.addons).filter(function (k) { return s.addons[k]; });
    // Sleeve deliberately excluded from this sum — it is not in CFG.addons and
    // would come back undefined, turning the total NaN.
    var addonsSum = addonKeys.reduce(function (t, k) {
      return k === 'sleeve' ? t : t + CFG.addons[k];
    }, 0);

    var sleeveOn = addonKeys.indexOf('sleeve') > -1;
    var sleeveUnit = sleeveOn ? sleevePrice(qty) : 0;

    var discountable = box.corp + (glasses ? CFG.glassesEx : 0) + addonsSum;
    var unitFull = discountable + sleeveUnit;

    var tierIdx = -1;
    CFG.volumeTiers.forEach(function (t, i) { if (qty >= t.min) tierIdx = i; });
    var pct = tierIdx >= 0 ? CFG.volumeTiers[tierIdx].pct : 0;
    // Round to cents BEFORE multiplying. Otherwise the per-box price shown and
    // the total shown are computed from different numbers, and a customer who
    // checks quantity x per-box finds it does not equal the total (89.99 at 5%
    // off is 85.4905: 50 x $85.49 = $4,274.50, but 50 x 85.4905 = $4,274.53).
    // This also matches how the invoice is actually raised.
    // Tier applies to the box, the glasses upgrade and the other add-ons. The
    // sleeve is added at its own rate AFTERWARDS, undiscounted. Both terms are
    // already exact cents, so qty x unit still equals total to the cent.
    var unit = cents(discountable * (1 - pct / 100)) + sleeveUnit;
    var total = cents(qty * unit);
    var deposit = cents(total * (CFG.depositPct / 100));

    // What the volume tier is worth to the buyer, in dollars. The sleeve is
    // priced outside the tier and sits in both unitFull and unit, so it
    // cancels here — this is the tier alone, never the sleeve break or the
    // free delivery, which are each stated in their own words elsewhere.
    var savingPerBox = cents(unitFull - unit);
    var saving = cents(qty * savingPerBox);

    var nextT = null;
    for (var i = 0; i < CFG.volumeTiers.length; i++) {
      if (CFG.volumeTiers[i].min > qty && CFG.volumeTiers[i].pct > pct) { nextT = CFG.volumeTiers[i]; break; }
    }

    // Progress toward the next tier, for the quantity bar. Against the final
    // tier's minimum so the bar always has a destination.
    var lastMin = CFG.volumeTiers[CFG.volumeTiers.length - 1].min;
    var barPct = Math.max(4, Math.min(100, Math.round(qty / lastMin * 100)));

    var boxCur = BOXES.filter(function (b) { return b.id === s.box; })[0];
    var sumBits = [qty + ' ' + (duo ? 'Deluxe Boxes' : 'Gift Boxes')];
    if (glasses) sumBits.push('N&N glasses');
    addonKeys.forEach(function (k) { sumBits.push(ADDON_SHORT[k]); });
    sumBits.push(s.delivery === 'central' ? 'one address' : 'individual addresses');

    return {
      qty: qty, moq: CFG.moq, duo: duo, glasses: glasses,
      pct: pct, tierIdx: tierIdx, nextT: nextT, barPct: barPct,
      unitFull: unitFull, unit: unit, total: total, deposit: deposit,
      sleeveOn: sleeveOn, sleeveUnit: sleeveUnit,
      sleeveBreakAt: CFG.sleeve.breakAt, sleeveAt: CFG.sleeve.at,
      saving: saving, savingPerBox: savingPerBox,
      addonKeys: addonKeys,
      boxName: boxCur.name,
      sumLine: sumBits.join(' · '),
      underMoq: qty < CFG.moq
    };
  }

  /* ============================================================
     VIEWS
     ============================================================ */

  /**
   * A toggle rebuilds the whole build panel, which destroys the very control
   * the user just activated — focus falls to <body> and a keyboard user is
   * thrown back to the top of the page on every selection. Re-focus the
   * equivalent control after the rebuild.
   */
  function renderKeepingFocus() {
    var a = document.activeElement;
    var sel = null;
    if (a && root.contains(a)) {
      var val = a.getAttribute && a.getAttribute('data-gb-value');
      if (a.hasAttribute && a.hasAttribute('data-gb-pick')) {
        sel = '[data-gb-pick="' + a.getAttribute('data-gb-pick') + '"][data-gb-value="' + val + '"]';
      } else if (a.hasAttribute && a.hasAttribute('data-gb-toggle')) {
        sel = '[data-gb-toggle="' + a.getAttribute('data-gb-toggle') + '"]' + (val ? '[data-gb-value="' + val + '"]' : '');
      }
    }
    render();
    if (!sel) return;
    var again = el(sel);
    if (again) {
      try { again.focus({ preventScroll: true }); } catch (e) { again.focus(); }
    }
  }

  /**
   * Picking a box rebuilds the list with the chosen box first, as the big
   * card. On a phone the buyer has usually scrolled down to the row they
   * tapped, so the card they chose lands above the screen and they had to
   * scroll back up to see it (Roscoe, 5 Oct 2026). Bring it to the top of
   * the view, under the fixed header, when it is out of sight.
   */
  function revealSelectedBox() {
    var card = el('.gb-boxwrap') || el('.gb-box--selected');
    if (!card) return;
    var header = document.querySelector('.grada-header');
    var offset = (header ? header.getBoundingClientRect().height : 0) + 12;
    var top = card.getBoundingClientRect().top;
    if (top >= offset && top < window.innerHeight * 0.4) return;
    window.scrollTo({
      top: Math.max(0, window.pageYOffset + top - offset),
      behavior: reducedMotion() ? 'auto' : 'smooth'
    });
  }

  function setView(v) {
    state.view = v;
    try {
      if (v === 'intro') history.pushState(null, '', location.pathname + location.search);
      else location.hash = v;
    } catch (e) {}
    render();
    window.scrollTo({ top: 0, behavior: 'auto' });
    focusView(v);
  }

  /**
   * Views swap in place, so without this focus stays on the control inside the
   * view we just hid: assistive tech announces nothing, and the next Tab
   * restarts from the top of the document. Moving focus to the new view's
   * heading announces the change and puts Tab back in the right place.
   */
  function focusView(v) {
    var node = el('[data-gb-view="' + v + '"]');
    if (!node) return;
    var target = node.querySelector('h1, h2') || node;
    target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  }

  /* ============================================================
     RENDER — build view
     ============================================================ */

  function tickSvg(size, width) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="' + width + '" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
  }

  function boxCardHtml(b, d) {
    var cfg = CFG.boxes[b.id];
    var selected = state.box === b.id;

    if (selected) {
      // Photo, then the name as an eyebrow over a display title, what is in
      // the box, and the price closing the card. The border already says it
      // is chosen, so the badge is a quiet tick: over the photo on a phone,
      // in the top corner on desktop.
      // The sleeve arrow cannot live inside the card, which is itself a
      // button, so the wrap holds both and the arrow sits over the photo.
      var sleeve = b.sleeve && CFG.img[b.sleeve];
      var back = sleeve && b.back && CFG.img[b.back];
      // Ticking the glasses upgrade shows the box with the pair, and the list
      // says so, so the photo and the words always match what is ordered.
      var withPair = !d.duo && d.glasses;
      var shot = CFG.img[(withPair && b.glassesImg) || b.img];
      var contents = withPair ? b.contents.map(function (item) {
        return item === 'Japanese Hand-Blown Coupe' ? 'Two Nick & Nora Glasses' : item;
      }) : b.contents;
      return '' +
        '<div class="gb-boxwrap' + (sleeve ? ' has-sleeve' : '') + (back ? ' has-back' : '') + (b.theme ? ' gb-boxwrap--t-' + b.theme : '') + '">' +
        '<button type="button" class="gb-box gb-box--selected' + (b.theme ? ' gb-box--t-' + b.theme : '') + '" role="radio" aria-checked="true" data-gb-pick="box" data-gb-value="' + b.id + '">' +
          '<span class="gb-box__photo" style="background-image:url(' + esc(shot) + ')' + (b.zoom ? ';--gb-zoom:' + b.zoom : '') + '">' +
            (sleeve ? '<span class="gb-box__sleeve" style="background-image:url(' + esc(sleeve) + ')"></span>' : '') +
            (back ? '<span class="gb-box__sleeve gb-box__back" style="background-image:url(' + esc(back) + ')"></span>' : '') +
          '</span>' +
          '<span class="gb-box__body">' +
            '<span class="gb-box__head">' +
              '<span class="gb-box__eyebrow">' + esc(b.eyebrow) + '</span>' +
              '<span class="gb-box__badge">' + tickSvg(12, 3.4) + 'Currently selected</span>' +
            '</span>' +
            '<span class="gb-box__title">' + esc(b.title) + '</span>' +
            (b.tagline ? '<span class="gb-box__tagline"><span class="gb-box__tagcaps">' + esc(b.tagline[0]) + '</span> <span class="gb-box__tagscript">' + esc(b.tagline[1]) + '</span></span>' : '') +
            '<span class="gb-box__sub">' + esc(b.cardSub) + '</span>' +
            '<span class="gb-box__label">In the box</span>' +
            '<span class="gb-box__list">' +
              contents.map(function (item) {
                return '<span class="gb-box__item">' + esc(item) + '</span>';
              }).join('') +
            '</span>' +
            '<span class="gb-box__pricerow">' +
              '<span class="gb-box__price">' + esc(money(cfg.corp)) + '</span>' +
              '<span class="gb-box__per">per box, inc GST</span>' +
            '</span>' +
          '</span>' +
        '</button>' +
        (sleeve ? '<button type="button" class="gb-flip" data-gb-flip aria-pressed="false" aria-label="Show the outside of the box">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>' +
          '</button>' +
          '<span class="gb-flipdots" aria-hidden="true"><i></i><i></i>' + (back ? '<i></i>' : '') + '</span>' : '') +
        '</div>';
    }

    return '' +
      '<button type="button" id="gb-boxopt-' + b.id + '" class="gb-box gb-box--row' + (b.theme ? ' gb-box--t-' + b.theme : '') + '" role="radio" aria-checked="false" data-gb-pick="box" data-gb-value="' + b.id + '">' +
        '<span class="gb-box__thumb" style="background-image:url(' + esc(CFG.img[b.img]) + ')' + (b.zoom ? ';--gb-zoom:' + b.zoom : '') + '"></span>' +
        '<span class="gb-box__rowtext">' +
          // Same voice as the selected card: the eyebrow over a display title.
          '<span class="gb-box__eyebrow">' + esc(b.eyebrow) + '</span>' +
          '<span class="gb-box__title gb-box__title--row">' + esc(b.title) + '</span>' +
          '<span class="gb-box__sub">' + esc(b.sub) + '</span>' +
          '<span class="gb-box__brief">' + esc(b.brief) + '</span>' +
          // The price closes the row, as it closes the selected card.
          '<span class="gb-box__pricerow gb-box__pricerow--row">' +
            '<span class="gb-box__price">' + esc(money(cfg.corp)) + '</span>' +
            '<span class="gb-box__per">per box, inc GST</span>' +
          '</span>' +
        '</span>' +
      '</button>';
  }

  function glassesRowHtml(d) {
    // Nested under the selected single-bottle box; Deluxe already has the pair.
    if (d.duo) return '';
    return '' +
      '<div class="gb-upgrade gb-upgrade--t-' + state.box + '">' +
        '<span class="gb-upgrade__elbow" aria-hidden="true"></span>' +
        '<button type="button" class="gb-upgrade__row" role="checkbox" aria-checked="' + (d.glasses ? 'true' : 'false') + '" data-gb-toggle="glasses">' +
          '<span class="gb-check' + (d.glasses ? ' is-on' : '') + '">' + (d.glasses ? tickSvg(14, 3) : '') + '</span>' +
          '<span class="gb-upgrade__thumb" style="background-image:url(' + esc(CFG.img.glasses) + ')"></span>' +
          '<span class="gb-upgrade__text">' +
            '<span class="gb-kicker">For your box</span>' +
            '<span class="gb-upgrade__name">Upgrade to Two Nick &amp; Nora Glasses</span>' +
          '</span>' +
          '<span class="gb-upgrade__price">+ ' + esc(money(CFG.glassesEx)) + '</span>' +
        '</button>' +
      '</div>';
  }

  /**
   * The sleeve, with their own logo on it.
   *
   * Until 21 Sep this drew an example sleeve carrying EG Funds' mark, which a
   * buyer read as somebody else's box: "where do I upload the logo? it comes
   * up with the EG logo as a placeholder" (Harry Bawa, 17 Sep). Now the buyer
   * puts their own logo on it, here, before the price and before the contact
   * form, because that is the moment they stop comparing and start owning it.
   */
  function sleevePreviewHtml() {
    if (!MOCKUP_ON) return '';

    var body;
    if (state.logoPreview) {
      body =
        '<img class="gb-mockup__img" src="' + esc(state.logoPreview) + '" alt="The corporate gift box with your logo in gold on the sleeve">' +
        '<div class="gb-mockup__bar">' +
          '<span class="gb-mockup__file">' + esc(state.logo ? state.logo.name : '') + '</span>' +
          '<span class="gb-mockup__acts">' +
            '<button type="button" class="gb-linkbtn" data-gb-logodownload>Download the mockup</button>' +
            '<button type="button" class="gb-linkbtn" data-gb-logopick>Change logo</button>' +
          '</span>' +
        '</div>';
    } else {
      // The example renders once, then sits in state so a re-render is instant.
      if (!state.examplePreview) {
        exampleSleeve().then(function (url) {
          if (!url || state.examplePreview) return;
          state.examplePreview = url;
          render();
        });
      }
      body =
        (state.examplePreview
          ? '<img class="gb-mockup__img gb-mockup__img--example" src="' + esc(state.examplePreview) + '" alt="Example of the corporate gift box, with a gold placeholder where your logo prints">'
          : '') +
        '<button type="button" class="gb-logodrop" data-gb-logopick' + (state.logoBusy ? ' disabled' : '') + '>' +
          '<span class="gb-logodrop__title">' + (state.logoBusy ? 'Putting it on the box…' : 'Add your logo') + '</span>' +
          '<span class="gb-logodrop__sub">PNG, SVG or JPG. See it on the box before you order.</span>' +
        '</button>';
    }

    return '' +
      '<div class="gb-mockup" data-gb-mockup>' +
        '<span class="gb-kicker">Your sleeve</span>' +
        body +
        (state.logoErr ? '<p class="gb-mockup__err" role="alert">' + esc(state.logoErr) + '</p>' : '') +
        '<p class="gb-mockup__note">Your logo prints in gold on the sleeve, not on the bottle. Print-ready artwork is collected after you order, and nothing prints until you approve the proof.</p>' +
        '<input type="file" hidden data-gb-logoinput accept="image/png,image/svg+xml,image/jpeg,image/webp">' +
      '</div>';
  }

  function addonRowHtml(a, d) {
    var on = !!state.addons[a.id];
    var isSleeve = a.id === 'sleeve';
    var inner = '' +
      '<button type="button" class="gb-addon__row" data-gb-toggle="addon" data-gb-value="' + a.id + '" role="checkbox" aria-checked="' + (on ? 'true' : 'false') + '">' +
        '<span class="gb-check' + (on ? ' is-on' : '') + '">' + (on ? tickSvg(14, 3) : '') + '</span>' +
        (a.img ? '<span class="gb-addon__thumb' + (a.contain ? ' gb-addon__thumb--bag' : '') + (a.zoom && CFG.img[a.zoom] ? ' gb-addon__thumb--zoom' : '') + '"' +
          (a.zoom && CFG.img[a.zoom] ? ' data-gb-zoom="' + esc(CFG.img[a.zoom]) + '" data-gb-zoomlabel="' + esc(a.name) + '"' : '') +
          ' style="background-image:url(' + esc(CFG.img[a.img]) + ')"></span>' : '') +
        '<span class="gb-addon__name">' + esc(a.name) + '</span>' +
        '<span class="gb-addon__price"' + (isSleeve ? ' data-gb-sleeveprice' : '') + '>+ ' +
          esc(money(isSleeve ? sleevePrice(d.qty) : CFG.addons[a.id])) + '</span>' +
      '</button>' +
      // Always rendered, hidden above the break: the quantity patch runs while
      // the buyer is typing, so it must only ever flip text and a hidden flag.
      // Inserting or removing a node there would reflow under the caret.
      (isSleeve
        ? '<p class="gb-addon__hint" data-gb-sleevehint' + (d.qty < d.sleeveBreakAt ? '' : ' hidden') + '>' +
            'Drops to ' + esc(money(d.sleeveAt)) + ' a box at ' + d.sleeveBreakAt + ' or more.</p>'
        : '') +
      (isSleeve && on ? sleevePreviewHtml() : '');

    return '<div class="gb-addon' + (on ? ' is-on' : '') + '">' + inner + '</div>';
  }

  function renderBuild(d) {
    var host = el('[data-gb-build]');
    if (!host) return;

    // The mockup lives inside this panel, so its state belongs in the key.
    // Without it the panel is considered unchanged when a logo arrives, and
    // the mockup is drawn but never shown.
    var key = [state.box, state.glasses ? 1 : 0,
      Object.keys(state.addons).map(function (k) { return state.addons[k] ? 1 : 0; }).join(''),
      state.logoPreview ? 'logo' : '', state.logoBusy ? 'busy' : '', state.logoErr ? 'err' : '',
      state.examplePreview ? 'eg' : ''].join('|');

    if (host.getAttribute('data-gb-key') !== key) {
      // Selected box first as the big card, the others as compact rows in
      // catalogue order, the glasses upgrade nested under the selection.
      var selected = BOXES.filter(function (b) { return b.id === state.box; })[0];
      var rest = BOXES.filter(function (b) { return b.id !== state.box; });

      host.innerHTML = '' +
        '<h2 class="gb-h2"><span class="gb-h2__display">Build</span> <span class="gb-h2__script">your</span> <span class="gb-h2__display">Box</span></h2>' +
        // The glasses upgrade is a checkbox, so it cannot live inside the
        // radiogroup; it sits between the selected box and the other two.
        // aria-owns pulls those two into the one group, so a screen reader
        // hears "1 of 3" rather than two groups (16 Oct 2026).
        '<div class="gb-opts" role="radiogroup" aria-label="Choose your box" aria-owns="' +
          rest.map(function (b) { return 'gb-boxopt-' + b.id; }).join(' ') + '">' +
          boxCardHtml(selected, d) +
        '</div>' +
        glassesRowHtml(d) +
        '<div class="gb-opts" role="none">' +
          rest.map(function (b) { return boxCardHtml(b, d); }).join('') +
        '</div>' +

        '<h3 class="gb-h3">How many?</h3>' +
        // Stacked on mobile; the wrapper lets them share a row on desktop.
        '<div class="gb-qtyrow">' +
          '<div class="gb-qty">' +
            '<button class="gb-qty__btn" type="button" data-gb-qty="dec" aria-label="Fewer boxes">&minus;</button>' +
            '<input class="gb-qty__input" type="text" inputmode="numeric" value="' + d.qty + '" data-gb-qtyinput aria-label="Number of boxes">' +
            '<button class="gb-qty__btn" type="button" data-gb-qty="inc" aria-label="More boxes">+</button>' +
          '</div>' +
          '<div class="gb-tiers" data-gb-tiers>' + tiersHtml(d) + '</div>' +
        '</div>' +
        '<div data-gb-moqnote>' + moqNoteHtml(d) + '</div>' +

        '<h3 class="gb-h3">Add extras</h3>' +
        '<p class="gb-lede gb-lede--tight">All optional.</p>' +
        '<div class="gb-addons" role="group" aria-label="Add extras to every box">' +
          ADDONS.map(function (a) { return addonRowHtml(a, d); }).join('') +
        '</div>';

      host.setAttribute('data-gb-key', key);
    } else {
      patchQty(d);
    }
  }

  function tiersHtml(d) {
    var head = '' +
      '<div class="gb-tiers__head">' +
        '<span class="gb-tiers__count">' + d.qty + ' box' + (d.qty === 1 ? '' : 'es') + (d.pct > 0 ? ' · ' + d.pct + '% off' : '') +
          (d.saving > 0 ? ' · <b>saves ' + esc(money(d.saving)) + '</b>' : '') + '</span>' +
        (d.nextT
          ? '<span class="gb-tiers__next">' + (d.nextT.min - d.qty) + ' more &rarr; <b>' + d.nextT.pct + '% off</b></span>'
          : '<span class="gb-tiers__next"><b>Best price unlocked</b></span>') +
      '</div>' +
      '<div class="gb-tiers__bar"><span style="width:' + d.barPct + '%"></span></div>';

    // These were <span>s: they read as tappable chips and were not, on any
    // device. Now they set the quantity to that tier — which is what everyone
    // tries to do with them.
    var cells = CFG.volumeTiers.map(function (t, i) {
      var cur = i === d.tierIdx;
      return '<button type="button" class="gb-tiers__cell' + (cur ? ' is-current' : '') + '"' +
        ' data-gb-pick="tier" data-gb-value="' + t.min + '"' +
        ' aria-pressed="' + (cur ? 'true' : 'false') + '"' +
        ' aria-label="' + t.min + ' boxes' + (t.pct > 0 ? ', ' + t.pct + '% off' : '') + '">' +
        '<b>' + t.min + '</b>' +
        '<span>' + (cur ? "You're here" : (t.pct > 0 ? t.pct + '% off' : 'Corporate')) + '</span>' +
      '</button>';
    }).join('');

    return head + '<div class="gb-tiers__cells">' + cells + '</div>';
  }

  function moqNoteHtml(d) {
    return d.underMoq
      ? '<div class="gb-alert" role="status"><b>Corporate orders start at ' + d.moq + ' boxes.</b> ' +
        'Under ' + d.moq + '? Grab our <a href="' + esc(CFG.retailUrl) + '">retail gift box</a> instead.</div>'
      : '';
  }

  function patchQty(d) {
    var input = el('[data-gb-qtyinput]');
    // Never write back into the field the user is typing in.
    if (input && document.activeElement !== input && input.value !== String(d.qty)) {
      input.value = String(d.qty);
    }
    var tiers = el('[data-gb-tiers]');
    if (tiers) tiers.innerHTML = tiersHtml(d);
    var note = el('[data-gb-moqnote]');
    if (note) note.innerHTML = moqNoteHtml(d);

    // The sleeve is the one add-on whose price depends on quantity. Its row is
    // built by renderBuild, which only re-runs when the structure key changes —
    // so without this the row would still read $10 at 200 boxes.
    var sp = el('[data-gb-sleeveprice]');
    if (sp) {
      var want = '+ ' + money(sleevePrice(d.qty));
      if (sp.textContent !== want) sp.textContent = want;
    }
    var sh = el('[data-gb-sleevehint]');
    if (sh) sh.hidden = d.qty >= d.sleeveBreakAt;
  }

  /* ============================================================
     RENDER — send view
     ============================================================ */

  function renderSend(d) {
    var host = el('[data-gb-delivery]');
    if (host) {
      host.innerHTML = '' +
        '<button type="button" class="gb-deliv' + (state.delivery === 'central' ? ' is-on' : '') + '" role="radio" aria-checked="' + (state.delivery === 'central') + '" data-gb-pick="delivery" data-gb-value="central">' +
          '<span class="gb-radio' + (state.delivery === 'central' ? ' is-on' : '') + '"></span>' +
          '<span class="gb-deliv__name">One address</span>' +
          '<span class="gb-deliv__price">Free</span>' +
        '</button>' +
        '<button type="button" class="gb-deliv gb-deliv--tall' + (state.delivery === 'individual' ? ' is-on' : '') + '" role="radio" aria-checked="' + (state.delivery === 'individual') + '" data-gb-pick="delivery" data-gb-value="individual">' +
          '<span class="gb-radio' + (state.delivery === 'individual' ? ' is-on' : '') + '"></span>' +
          '<span class="gb-deliv__text">' +
            '<span class="gb-deliv__name">Individual addresses</span>' +
            '<span class="gb-deliv__desc">We send you an address sheet after you order. Shipping is confirmed with you before we charge it.</span>' +
          '</span>' +
          '<span class="gb-deliv__est"><span>from ' + esc(money(CFG.individualEstFrom)) + '</span><span>a box, est.</span></span>' +
        '</button>';
    }

    var intentHost = el('[data-gb-intent]');
    if (intentHost) {
      var opts = [
        {
          id: 'order',
          name: 'Confirm my order',
          desc: 'We email your invoice straight away. Pay the ' + CFG.depositPct + '% deposit (' +
                money(d.deposit) + ') within ' + CFG.depositHours + ' hours to secure your order and production slot. ' +
                'The balance is invoiced on ' + CFG.finalInvoice + '.',
          tag: money(d.deposit), tagSub: 'deposit'
        },
        {
          id: 'quote',
          name: 'Email me a quote',
          desc: 'A formal quote for your records. Nothing is booked and no payment is due.',
          tag: 'No obligation', tagSub: ''
        }
      ];
      // Laurie mode: the order alone, shown as what happens next rather than
      // as a one-option choice, so the deposit terms still sit above the form.
      if (!QUOTE_OFFERED) opts = opts.slice(0, 1);
      if (QUOTE_OFFERED) {
        intentHost.setAttribute('role', 'radiogroup');
        intentHost.setAttribute('aria-label', 'How would you like to proceed?');
      } else {
        intentHost.removeAttribute('role');
        intentHost.removeAttribute('aria-label');
      }
      intentHost.innerHTML = '<h3 class="gb-h3">Ready to go?</h3>' +
        opts.map(function (o) {
          var on = state.intent === o.id;
          var body = '<span class="gb-deliv__text">' +
                '<span class="gb-deliv__name">' + esc(o.name) + '</span>' +
                '<span class="gb-deliv__desc">' + esc(o.desc) + '</span>' +
              '</span>' +
              '<span class="gb-deliv__est"><span>' + esc(o.tag) + '</span>' +
                (o.tagSub ? '<span>' + esc(o.tagSub) + '</span>' : '') + '</span>';
          if (!QUOTE_OFFERED) return '<div class="gb-deliv gb-deliv--tall gb-deliv--only is-on">' + body + '</div>';
          return '<button type="button" class="gb-deliv gb-deliv--tall' + (on ? ' is-on' : '') + '"' +
            ' role="radio" aria-checked="' + on + '" data-gb-pick="intent" data-gb-value="' + o.id + '">' +
              '<span class="gb-radio' + (on ? ' is-on' : '') + '"></span>' + body +
            '</button>';
        }).join('');
    }

    var sumline = el('[data-gb-sumline]');
    if (sumline) sumline.textContent = d.sumLine;
    var sumtotal = el('[data-gb-sumtotal]');
    if (sumtotal) sumtotal.textContent = money(d.total) + ' inc GST';
    var sumsaving = el('[data-gb-sumsaving]');
    if (sumsaving) {
      sumsaving.hidden = !(d.saving > 0);
      if (d.saving > 0) sumsaving.textContent = 'You save ' + money(d.saving) + ' with the ' + d.pct + '% volume tier';
    }
    var freeship = el('[data-gb-freeship]');
    if (freeship) freeship.hidden = state.delivery !== 'central';

    var submit = el('[data-gb-submit]');
    if (submit) {
      submit.textContent = CFG.checkoutMode === 'cart'
        ? 'Add to cart · ' + money(d.total)
        : (state.intent === 'quote' ? 'Email me a quote' : 'Confirm my order · ' + money(d.total));
      submit.disabled = d.underMoq;
    }

    var hint = el('[data-gb-submithint]');
    if (hint) {
      hint.hidden = !d.underMoq;
      hint.textContent = 'Orders start at ' + d.moq + ' boxes.';
    }

    var reassure = el('[data-gb-reassure]');
    if (reassure) {
      reassure.textContent = CFG.checkoutMode === 'laurie'
        ? 'Your invoice is emailed straight away. Your order is confirmed once the deposit is paid, within ' + CFG.depositHours + ' hours.'
        : (state.intent === 'quote'
            ? 'We email your quote within one business day. Nothing is booked until you say so.'
            : 'We email your invoice within one business day. Nothing prints until you approve the proof.');
    }
  }

  /* ============================================================
     RENDER — top level
     ============================================================ */

  function render() {
    // A quote saved from an earlier visit must not survive into laurie mode.
    if (!QUOTE_OFFERED) state.intent = 'order';
    var d = derive();

    ['intro', 'build', 'send', 'done'].forEach(function (v) {
      var node = el('[data-gb-view="' + v + '"]');
      if (node) node.hidden = state.view !== v;
    });

    if (state.view === 'build') {
      renderBuild(d);
      renderBar(d);
    }
    if (state.view === 'send') renderSend(d);
    if (state.view === 'done') renderDone(d);

    // Nothing to restore from the confirmation screen, and persisting here
    // would immediately undo the clearPersisted() that ran on success.
    if (state.view !== 'done') savePersisted();
  }

  function renderBar(d) {
    var bar = el('[data-gb-bar]');
    if (!bar) return;
    bar.hidden = false;

    var figures = el('[data-gb-barfigures]');
    if (figures) {
      figures.innerHTML = '' +
        '<span class="gb-bar__total">' + esc(money(d.total)) + '</span>' +
        '<br><span class="gb-bar__line">' + d.qty + (d.qty === 1 ? ' box' : ' boxes') + ' · inc GST' +
          (d.pct > 0 ? ' · ' + d.pct + '% off' : '') +
          (d.saving > 0 ? ' · <b class="gb-bar__saving">saves ' + esc(money(d.saving)) + '</b>' : '') + '</span>';
    }

    var cta = el('[data-gb-next]');
    if (cta) cta.disabled = d.underMoq;
  }

  function renderDone(d) {
    var saved = el('[data-gb-savedline]');
    var summary = el('[data-gb-donesummary]');

    // After a contact-form POST the page has reloaded, so read back what we
    // stashed at submit time rather than the (now reset) live state.
    var snap = readSnapshot();
    // A snapshot from an older build can be missing fields; rendering it raw
    // puts the word "undefined" in front of the customer.
    if (snap && (snap.total === undefined || snap.sumLine === undefined)) snap = null;
    if (!snap) {
      if (summary) summary.hidden = true;
      if (saved) saved.textContent = '';
      return;
    }

    if (saved) {
      saved.textContent = snap.pct > 0
        ? ' Your ' + snap.pct + '% volume discount is locked into this price.'
        : '';
    }

    var title = el('[data-gb-donetitle]');
    var lede = el('[data-gb-donelede]');
    var orderName = el('[data-gb-ordername]');
    var invoice = el('[data-gb-invoice]');

    if (snap.invoiceUrl) {
      if (title) title.textContent = 'Your invoice is on its way.';
      if (lede) {
        lede.innerHTML = 'Pay the ' + esc(snap.deposit) + ' deposit within ' + CFG.depositHours +
          ' hours to secure your order and production slot.' +
          '<span data-gb-savedline>' + esc(saved ? saved.textContent : '') + '</span>' +
          ' The final invoice follows on ' + esc(CFG.finalInvoice) + ', and paying it releases your boxes for Christmas delivery.' +
          ' Nothing prints until you approve the proof of your sleeve.';
      }
      if (orderName && snap.orderName) {
        orderName.hidden = false;
        orderName.textContent = 'Order ' + snap.orderName;
      }
      if (invoice) {
        invoice.hidden = false;
        invoice.href = snap.invoiceUrl;
      }
    } else {
      if (orderName) orderName.hidden = true;
      if (invoice) invoice.hidden = true;
      // Quote mode: the wording depends on which path they picked.
      if (title) title.textContent = snap.intent === 'quote' ? 'Quote on its way.' : 'Order received.';
      if (lede) {
        lede.textContent = snap.intent === 'quote'
          ? 'We email your quote within one business day. Nothing is booked until you say so.'
          : 'We email your invoice within one business day. Pay the ' + snap.deposit +
            ' deposit within ' + CFG.depositHours + ' hours to secure your order. The balance is invoiced on ' + CFG.finalInvoice + '.';
      }
    }

    if (summary) {
      summary.hidden = false;
      var rows = [
        ['Order', snap.sumLine],
        ['Boxes', String(snap.qty)],
        ['Per box', snap.perBox]
      ];
      if (snap.intent === 'order') rows.push(['Deposit to confirm', snap.deposit]);
      if (snap.pct > 0) rows.push(['Volume discount', snap.pct + '%' + (snap.saving ? ' · you saved ' + snap.saving : '')]);
      rows.push(['Total', snap.total]);

      summary.innerHTML = rows.map(function (r) {
        return '<div class="gb-sumrow"><span>' + esc(r[0]) + '</span><b>' + esc(r[1]) + '</b></div>';
      }).join('') + '<p class="gb-sumfoot">All prices include GST' +
        (snap.delivery === 'individual' ? ' · individual shipping confirmed before charging' : '') + '</p>';
    }
  }

  /* ============================================================
     SNAPSHOT — survives the contact form's page reload
     ============================================================ */

  function buildSnapshot(d) {
    return {
      qty: d.qty,
      sumLine: d.sumLine,
      delivery: state.delivery,
      intent: state.intent,
      deposit: money(d.deposit),
      pct: d.pct,
      saving: d.saving > 0 ? money(d.saving) : '',
      perBox: money(d.unit),
      total: money(d.total)
    };
  }

  function writeSnapshot(snap) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snap)); } catch (e) {}
  }

  function readSnapshot() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function clearSnapshot() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /* ============================================================
     CART MAPPING — the box is the only variant; everything else rides as
     line item properties. Cart mode cannot charge upgrades, add-ons,
     discounts or delivery.
     ============================================================ */

  function buildCartItems(d) {
    var variantId = CFG.boxes[state.box].variantId;
    if (!variantId) return null;

    return [{
      id: String(variantId),
      quantity: d.qty,
      properties: {
        'Configuration': d.sumLine,
        'Glasses upgrade': d.glasses ? 'Yes' : 'No'
      }
    }];
  }

  /* ============================================================
     LAURIE ORDERING — posts the configuration only; Laurie prices it from
     the same pricing source of truth, raises a Shopify draft order and
     emails the invoice.
     ============================================================ */

  function laurieContact() {
    var form = el('#gift-builder-form');
    if (!form) return null;
    var val = function (name) {
      var input = form.querySelector('[name="contact[' + name + ']"]');
      return input ? input.value.trim() : '';
    };
    return {
      name: val('Name'), company: val('Company'), email: val('email'),
      mobile: auMobile(val('Mobile')) || val('Mobile')
    };
  }

  function submitToLaurie(d, button) {
    if (d.underMoq) return;

    var form = el('#gift-builder-form');
    var hint = el('[data-gb-submithint]');

    if (form && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    var endpoint = (CFG.laurieEndpoint || '').trim();
    if (!endpoint) {
      if (hint) { hint.hidden = false; hint.textContent = 'Ordering is not configured yet.'; }
      return;
    }

    var contact = laurieContact();
    if (hint) hint.hidden = true;
    if (button) { button.disabled = true; button.textContent = 'Sending…'; }

    var ref = makeRef();

    // The artwork goes first when an endpoint is configured, so the order
    // arrives with a link to it rather than a promise to chase. sendLogo()
    // resolves to '' on any failure or after 8s, so a stalled upload can
    // never cost the order itself.
    sendLogo(ref).then(function (logoUrl) {
    fetch(endpoint, {
      method: 'POST',
      // NOT application/json. That is a non-simple Content-Type, so the browser
      // sends a CORS preflight OPTIONS first — and Apps Script web apps do not
      // answer OPTIONS, so every order would fail before it left the page. The
      // body is still JSON; only the header is relaxed to keep it a simple
      // request. The Apps Script side parses e.postData.contents regardless.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        reference: ref,
        config: {
          qty: d.qty,
          box: state.box,
          glasses: d.glasses,
          addons: d.addonKeys,
          delivery: state.delivery,
          intent: state.intent,
          logo: state.logo ? { name: state.logo.name, type: state.logo.type, url: logoUrl } : null
        },
        contact: contact
      })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          // Apps Script web apps answer 200 even when the script threw, so
          // res.ok is always true and checking it made this dead code: a failed
          // order would have shown the customer a confirmation screen. The
          // backend states its own outcome in body.ok.
          if (!body || !body.ok) throw new Error((body && body.error) || 'Could not place the order.');
          return body;
        });
      })
      .then(function (body) {
        writeSnapshot(Object.assign(buildSnapshot(d), {
          orderName: body.orderName,
          invoiceUrl: body.invoiceUrl
        }));
        clearPersisted();
        setView('done');
      })
      .catch(function (e) {
        if (hint) { hint.hidden = false; hint.textContent = e.message || 'Could not place the order.'; }
        if (button) { button.disabled = false; button.textContent = state.intent === 'quote' ? 'Email me a quote' : 'Confirm my order · ' + money(d.total); }
      });
    });
  }

  function submitToCart(d, button) {
    if (d.underMoq) return;

    var items = buildCartItems(d);
    var hint = el('[data-gb-submithint]');
    if (!items) {
      if (hint) { hint.hidden = false; hint.textContent = 'No variant ID configured for the ' + d.boxName + '.'; }
      return;
    }

    if (button) { button.disabled = true; button.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
        clearPersisted();
        window.location.href = '/cart';
      });
    }).catch(function (e) {
      if (hint) { hint.hidden = false; hint.textContent = e.message || 'Could not add to cart.'; }
      if (button) { button.disabled = false; button.textContent = 'Add to cart · ' + money(d.total); }
    });
  }

  /* ============================================================
     EVENTS
     ============================================================ */

  // Click the chosen box's photo and it grows out of the card into a full
  // screen viewer: the box, its front and its back, swiped or arrowed through,
  // closed by a tap, Esc, the close button or a downward swipe, shrinking back
  // into the card on the view it was left on. Capture phase, so the card's
  // own pick handler never sees the click.
  var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bgUrl(node) {
    var m = node && getComputedStyle(node).backgroundImage.match(/url\(["']?(.*?)["']?\)/);
    return m ? m[1] : '';
  }

  function viewsOf(wrap, photo) {
    var list = [{ src: bgUrl(photo), label: 'Inside the box', cover: true }];
    var front = photo.querySelector('.gb-box__sleeve:not(.gb-box__back)');
    var back = photo.querySelector('.gb-box__back');
    if (front) list.push({ src: bgUrl(front), label: 'The front' });
    if (back) list.push({ src: bgUrl(back), label: 'The back' });
    return list;
  }

  // Where the picture actually sits inside the card's photo for a view: the
  // inside shot fills it, the sleeve shots are letterboxed at 4:5.
  function cardRect(photo, v) {
    var r = photo.getBoundingClientRect();
    if (v.cover) return { left: r.left, top: r.top, width: r.width, height: r.height };
    var w = Math.min(r.width, r.height * 0.8), h = w / 0.8;
    return { left: r.left + (r.width - w) / 2, top: r.top + (r.height - h) / 2, width: w, height: h };
  }

  function stageRect() {
    var vw = window.innerWidth, vh = window.innerHeight;
    var pad = vw < 700 ? 16 : 48;
    var h = Math.min(vh - pad * 2 - 124, (vw - pad * 2) / 0.8, 1000);
    var w = h * 0.8;
    return { left: (vw - w) / 2, top: (vh - h - 124) / 2, width: w, height: h };
  }

  function px(r) { return { left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px' }; }

  function setCardView(wrap, i, views) {
    wrap.setAttribute('data-view', i);
    wrap.classList.toggle('is-sleeve', i >= 1);
    wrap.classList.toggle('is-back', i === 2);
    var f = wrap.querySelector('[data-gb-flip]');
    if (f) {
      f.setAttribute('aria-pressed', i ? 'true' : 'false');
      f.setAttribute('aria-label', i === views - 1 ? 'Show inside the box' : (i === 0 ? 'Show the front of the box' : 'Show the back of the box'));
    }
  }

  // opts: source (the element it grows from and returns to), views, index,
  // title, label (the dialog's name), theme, rectFor(i) (where view i sits on
  // the page) and onClose(i).
  function openViewer(opts) {
    var photo = opts.source;
    var views = opts.views;
    var index = opts.index || 0;
    var title = opts.title;
    var theme = opts.theme || 'so';
    var cardRect = function (p, v) { return opts.rectFor(views.indexOf(v)); };

    var ov = document.createElement('div');
    ov.className = 'gb-viewer gb-viewer--t-' + theme;
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', opts.label || title);
    ov.innerHTML =
      '<div class="gb-viewer__haze"></div>' +
      '<div class="gb-viewer__stage">' +
        '<div class="gb-viewer__track">' +
          views.map(function (v, i) {
            return '<div class="gb-viewer__slide" role="img" aria-label="' + esc(title + ', ' + v.label.toLowerCase()) + '" style="background-image:url(' + esc(v.src) + ')"></div>';
          }).join('') +
        '</div>' +
      '</div>' +
      '<div class="gb-viewer__chrome">' +
        '<button type="button" class="gb-viewer__btn gb-viewer__close" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        (views.length > 1 ?
          '<div class="gb-viewer__foot"><span class="gb-viewer__label" aria-live="polite"></span><span class="gb-viewer__sub"></span>' +
            '<span class="gb-viewer__row">' +
              '<button type="button" class="gb-viewer__btn gb-viewer__nav gb-viewer__nav--prev" aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg></button>' +
              '<span class="gb-viewer__dots">' + views.map(function () { return '<i></i>'; }).join('') + '</span>' +
              '<button type="button" class="gb-viewer__btn gb-viewer__nav gb-viewer__nav--next" aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></button>' +
            '</span></div>' : '') +
      '</div>';

    var haze = ov.querySelector('.gb-viewer__haze');
    var stage = ov.querySelector('.gb-viewer__stage');
    var track = ov.querySelector('.gb-viewer__track');
    var chrome = ov.querySelector('.gb-viewer__chrome');
    var label = ov.querySelector('.gb-viewer__label');
    var sub = ov.querySelector('.gb-viewer__sub');
    var dots = ov.querySelectorAll('.gb-viewer__dots i');
    var returnFocus = document.activeElement;
    var closing = false;

    function mark(i) {
      index = i;
      if (label) label.textContent = views[i].label;
      if (sub) sub.textContent = views[i].sub || '';
      for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-on', d === i);
      var p = ov.querySelector('.gb-viewer__nav--prev'), n = ov.querySelector('.gb-viewer__nav--next');
      if (p) p.disabled = i === 0;
      if (n) n.disabled = i === views.length - 1;
    }
    function go(i) {
      i = Math.max(0, Math.min(views.length - 1, i));
      track.scrollTo({ left: i * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      mark(i);
    }

    var from = cardRect(photo, views[index]);
    var to = stageRect();
    var foot = ov.querySelector('.gb-viewer__foot');
    function place(r) {
      Object.assign(stage.style, px(r));
      if (foot) foot.style.top = (r.top + r.height + 22) + 'px';
    }
    place(to);
    document.body.appendChild(ov);
    document.documentElement.classList.add('gb-viewer-open');
    track.scrollLeft = index * track.clientWidth;
    mark(index);
    photo.style.visibility = 'hidden';

    if (reduceMotion) {
      ov.classList.add('is-open');
    } else {
      stage.animate([
        Object.assign(px(from), { borderRadius: '0px' }),
        Object.assign(px(to), { borderRadius: '0px' })
      ], { duration: 520, easing: EASE });
      haze.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 380, easing: 'ease-out' });
      chrome.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 220, easing: 'ease-out', fill: 'backwards' });
      ov.classList.add('is-open');
    }
    ov.querySelector('.gb-viewer__close').focus({ preventScroll: true });

    var t;
    track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(function () { mark(Math.round(track.scrollLeft / track.clientWidth)); }, 60);
    }, { passive: true });

    function close(fromRect) {
      if (closing) return;
      closing = true;
      document.removeEventListener('keydown', onKey);
      if (opts.onClose) opts.onClose(index);
      var start = fromRect || stage.getBoundingClientRect();
      var end = cardRect(photo, views[index]);
      function done() {
        photo.style.visibility = '';
        ov.remove();
        document.documentElement.classList.remove('gb-viewer-open');
        if (returnFocus && returnFocus.focus) returnFocus.focus({ preventScroll: true });
      }
      if (reduceMotion) return done();
      chrome.animate([{ opacity: getComputedStyle(chrome).opacity }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      haze.animate([{ opacity: getComputedStyle(haze).opacity }, { opacity: 0 }], { duration: 420, easing: 'ease-out', fill: 'forwards' });
      stage.style.transform = '';
      stage.animate([
        Object.assign(px(start), { borderRadius: '0px' }),
        Object.assign(px(end), { borderRadius: '0px' })
      ], { duration: 460, easing: EASE, fill: 'forwards' }).onfinish = done;
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
      else if (e.key === 'Tab') {
        var f = [].filter.call(ov.querySelectorAll('button'), function (b) { return !b.disabled; });
        var i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    }
    document.addEventListener('keydown', onKey);

    ov.addEventListener('click', function (e) {
      if (e.target.closest('.gb-viewer__close')) return close();
      if (e.target.closest('.gb-viewer__nav--prev')) return go(index - 1);
      if (e.target.closest('.gb-viewer__nav--next')) return go(index + 1);
      if (!e.target.closest('.gb-viewer__stage, .gb-viewer__foot')) close();
    });

    // Swipe down to dismiss, the way Photos does: the picture follows the
    // finger and shrinks a touch, the haze thins, and past a threshold it goes.
    var y0 = null, x0 = 0, dy = 0, vertical = null;
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      y0 = e.clientY; x0 = e.clientX; dy = 0; vertical = null;
    });
    stage.addEventListener('pointermove', function (e) {
      if (y0 === null) return;
      var ddx = e.clientX - x0, ddy = e.clientY - y0;
      if (vertical === null && Math.abs(ddx) + Math.abs(ddy) > 8) vertical = Math.abs(ddy) > Math.abs(ddx);
      if (!vertical) return;
      dy = Math.max(0, ddy);
      var k = Math.min(1, dy / 400);
      stage.style.transform = 'translateY(' + dy + 'px) scale(' + (1 - k * 0.25) + ')';
      haze.style.opacity = String(1 - k);
      chrome.style.opacity = String(1 - k * 2);
    });
    function release() {
      if (y0 === null) return;
      y0 = null;
      if (vertical && dy > 110) return close(stage.getBoundingClientRect());
      stage.style.transition = 'transform 0.35s ' + EASE;
      stage.style.transform = '';
      haze.style.opacity = '';
      chrome.style.opacity = '';
      setTimeout(function () { stage.style.transition = ''; }, 360);
    }
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);

    window.addEventListener('resize', function onResize() {
      if (!ov.isConnected) return window.removeEventListener('resize', onResize);
      place(stageRect());
      track.scrollLeft = index * track.clientWidth;
    });
  }

  root.addEventListener('click', function (ev) {
    // On a phone, where the pour, shake, serve column is hidden, "How to make
    // it" opens the same three photos in the viewer, with their lines.
    var howto = ev.target.closest('[data-gb-howto]');
    if (howto && root.contains(howto)) {
      ev.preventDefault();
      ev.stopPropagation();
      var serve = root.querySelector('[data-gb-serve]');
      if (!serve) return;
      var frames = serve.querySelectorAll('.gb-serve__frame img');
      var steps = serve.querySelectorAll('.gb-serve__step');
      var hv = Array.prototype.map.call(frames, function (img, k) {
        var st = steps[k];
        return {
          src: img.currentSrc || img.src,
          label: st ? st.querySelector('.gb-serve__num').textContent + ' ' + st.querySelector('.gb-serve__word').textContent : '',
          sub: st ? st.querySelector('.gb-serve__line').textContent : '',
          cover: true
        };
      });
      openViewer({
        source: howto.querySelector('.gb-howto__stack') || howto,
        views: hv,
        title: 'Pour, shake, serve',
        label: 'How to make the espresso martini',
        theme: 'so',
        rectFor: function () {
          var r = (howto.querySelector('.gb-howto__stack') || howto).getBoundingClientRect();
          return { left: r.left, top: r.top, width: r.width, height: r.height };
        }
      });
      return;
    }
    var thumb = ev.target.closest('[data-gb-zoom]');
    if (thumb && root.contains(thumb)) {
      ev.preventDefault();
      ev.stopPropagation();
      var name = thumb.getAttribute('data-gb-zoomlabel') || '';
      openViewer({
        source: thumb,
        views: [{ src: thumb.getAttribute('data-gb-zoom'), label: name, cover: true }],
        title: name,
        label: name,
        theme: 'so',
        rectFor: function () {
          var r = thumb.getBoundingClientRect();
          return { left: r.left, top: r.top, width: r.width, height: r.height };
        }
      });
      return;
    }
    var photo = ev.target.closest('.gb-box--selected .gb-box__photo');
    if (!photo || !root.contains(photo)) return;
    ev.preventDefault();
    ev.stopPropagation();
    var wrap = photo.closest('.gb-boxwrap');
    if (!wrap) return;
    var views = viewsOf(wrap, photo);
    var boxTitle = (wrap.querySelector('.gb-box__title') || {}).textContent || 'Gift box';
    openViewer({
      source: photo,
      views: views,
      index: +wrap.getAttribute('data-view') || 0,
      title: boxTitle,
      label: boxTitle + ' gift box',
      theme: (wrap.className.match(/gb-boxwrap--t-(\w+)/) || [])[1] || 'so',
      rectFor: function (i) { return cardRect(photo, views[i]); },
      onClose: function (i) { setCardView(wrap, i, views.length); }
    });
  }, true);

  // Inside and outside of the selected box. No re-render: the class swaps
  // which photo shows, and any rebuild of the panel starts on the inside again.
  root.addEventListener('click', function (ev) {
    var f = ev.target.closest('[data-gb-flip]');
    if (!f || !root.contains(f)) return;
    var wrap = f.closest('.gb-boxwrap');
    // Inside, then the front of the box, then its back where there is one.
    var views = wrap.classList.contains('has-back') ? 3 : 2;
    var view = ((+wrap.getAttribute('data-view') || 0) + 1) % views;
    wrap.setAttribute('data-view', view);
    wrap.classList.toggle('is-sleeve', view >= 1);
    wrap.classList.toggle('is-back', view === 2);
    var last = view === views - 1;
    f.setAttribute('aria-pressed', view ? 'true' : 'false');
    f.setAttribute('aria-label', last ? 'Show inside the box' : (view === 0 ? 'Show the front of the box' : 'Show the back of the box'));
  });

  root.addEventListener('click', function (ev) {
    var t = ev.target.closest('[data-gb-start], [data-gb-restart], [data-gb-backtobuild],' +
      '[data-gb-pick], [data-gb-toggle], [data-gb-qty], [data-gb-next],' +
      '[data-gb-logopick], [data-gb-logodownload]');
    if (!t || !root.contains(t)) return;

    var d = derive();

    if (t.hasAttribute('data-gb-start')) { setView('build'); return; }

    if (t.hasAttribute('data-gb-backtobuild')) { setView('build'); return; }

    if (t.hasAttribute('data-gb-restart')) {
      state = freshState();
      clearSnapshot();
      clearPersisted();
      setView('intro');
      return;
    }

    if (t.hasAttribute('data-gb-pick')) {
      var group = t.getAttribute('data-gb-pick');
      var value = t.getAttribute('data-gb-value');
      if (group === 'box') {
        var changed = state.box !== value;
        state.box = value;
        if (value === 'deluxe') state.glasses = false;
        renderKeepingFocus();
        if (changed) revealSelectedBox();
        return;
      }
      if (group === 'delivery') {
        state.delivery = value;
        renderKeepingFocus();
        return;
      }
      if (group === 'intent') {
        state.intent = value;
        renderKeepingFocus();
        return;
      }
      if (group === 'tier') {
        var toQty = num(value, state.qty);
        if (toQty > 0) {
          state.qty = Math.min(MAX_QTY, toQty);
          renderKeepingFocus();
        }
        return;
      }
      return;
    }

    if (t.hasAttribute('data-gb-toggle')) {
      var kind = t.getAttribute('data-gb-toggle');
      if (kind === 'glasses') { state.glasses = !state.glasses; renderKeepingFocus(); return; }
      if (kind === 'addon') {
        var id = t.getAttribute('data-gb-value');
        state.addons[id] = !state.addons[id];
        renderKeepingFocus();
        return;
      }
      return;
    }

    if (t.hasAttribute('data-gb-qty')) {
      var dir = t.getAttribute('data-gb-qty');
      state.qty = dir === 'inc' ? state.qty + 1 : Math.max(1, state.qty - 1);
      renderKeepingFocus();
      return;
    }

    if (t.hasAttribute('data-gb-logopick')) {
      var picker = el('[data-gb-logoinput]');
      if (picker) picker.click();
      return;
    }

    if (t.hasAttribute('data-gb-logodownload')) {
      downloadMockup();
      return;
    }

    if (t.hasAttribute('data-gb-next')) {
      if (d.underMoq) return;
      setView('send');
    }
  });

  root.addEventListener('change', function (ev) {
    if (!ev.target.matches('[data-gb-logoinput]')) return;
    acceptLogo(ev.target.files && ev.target.files[0]);
    // Clear it so choosing the same file twice still fires a change.
    ev.target.value = '';
  });

  root.addEventListener('input', function (ev) {
    if (!ev.target.matches('[data-gb-qtyinput]')) return;
    var v = parseInt(ev.target.value, 10);
    state.qty = isNaN(v) ? 0 : Math.min(MAX_QTY, Math.max(0, v));
    // Quantity changes patch in place, so the field keeps focus.
    render();
  });

  // Once they leave the box, it shows the number being priced: "12.5" becomes 12, "10001"
  // becomes 10000, a blank or a letter becomes 0 (17 Oct 2026).
  root.addEventListener('change', function (ev) {
    if (!ev.target.matches('[data-gb-qtyinput]')) return;
    ev.target.value = String(state.qty);
  });

  // Fill the hidden fields at submit time and stash a snapshot for the
  // confirmation screen, which renders after Shopify's POST/redirect.
  var quoteForm = el('#gift-builder-form');
  // Quote mode hands the POST to Shopify, so nothing here disables the button
  // on the way out — without a guard an impatient double-tap on a slow
  // connection emails the same order twice.
  var submitting = false;
  // Cleared when the POST actually navigates away; see the watchdog below.
  var submitWatchdog = null;
  window.addEventListener('pagehide', function () { clearTimeout(submitWatchdog); });
  if (quoteForm) {
    quoteForm.addEventListener('submit', function (ev) {
      if (submitting) { ev.preventDefault(); return; }
      var d = derive();

      if (d.underMoq) { ev.preventDefault(); setView('build'); return; }

      if (CFG.checkoutMode === 'laurie') {
        ev.preventDefault();
        submitToLaurie(d, el('[data-gb-submit]'));
        return;
      }
      if (CFG.checkoutMode === 'cart') {
        ev.preventDefault();
        submitToCart(d, el('[data-gb-submit]'));
        return;
      }

      var isQuote = state.intent === 'quote';
      var balance = cents(d.total - d.deposit);

      // A short human reference both sides can quote back. Date-and-time based
      // rather than random so it sorts, and so two orders a minute apart never
      // collide in practice.
      var ref = makeRef();

      // The builder asks for three fields on purpose, so the notification
      // carries the follow-up list instead of leaving it to be re-derived.
      var needed = ['Delivery address', 'ABN + billing details for the invoice'];
      if (state.addons.sleeve) needed.push('Logo artwork for the sleeve');
      if (state.addons.card) needed.push('Wording for the handwritten note');
      if (state.delivery === 'individual') needed.push('Completed address sheet');

      var logoLine = state.logo
        ? state.logo.name + ' — uploaded in the builder, mockup approved on screen. Print-ready artwork still to collect.'
        : (state.addons.sleeve ? 'None yet — collect artwork for the sleeve' : 'n/a — no branded sleeve');

      var values = {
        logo: logoLine,
        qty: String(d.qty),
        box: d.boxName,
        glasses: d.duo ? 'Included — Deluxe ships with the pair' : (d.glasses ? 'Yes — two Nick & Nora glasses (+' + money(CFG.glassesEx) + '/box)' : 'No — coupe'),
        // ADDON_SHORT is lowercase because it also reads mid-sentence in the
        // on-page summary; capitalise here rather than changing the shared copy.
        addons: d.addonKeys.length
          ? d.addonKeys.map(function (k) {
              var t = ADDON_SHORT[k];
              return t.charAt(0).toUpperCase() + t.slice(1);
            }).join(', ')
          : 'None',
        delivery: state.delivery === 'central'
          ? 'One address — free (address to confirm)'
          : 'Individual addresses — est from ' + money(CFG.individualEstFrom) + '/box, confirm before charging',
        intent: isQuote
          ? 'QUOTE ONLY — no payment due, nothing booked'
          : 'CONFIRM ORDER — raise invoice, hold production slot',
        deposit: isQuote
          ? 'n/a — quote only'
          : money(d.deposit) + ' (' + CFG.depositPct + '%), payable within ' + CFG.depositHours + ' hours to confirm',
        balance: isQuote
          ? 'n/a — quote only'
          : money(balance) + ' — due before dispatch',
        timeline: 'Order by ' + CFG.orderBy + ' — receive in ' + CFG.receiveBy,
        ref: ref,
        needed: needed.join(' · '),
        tier: d.pct > 0 ? d.pct + '% off (' + d.qty + ' boxes) — saves ' + money(d.saving) : 'No volume discount',
        perbox: money(d.unit) + ' inc GST',
        total: money(d.total) + ' inc GST',
        json: JSON.stringify({
          qty: d.qty, box: state.box, glasses: d.glasses, addons: d.addonKeys,
          delivery: state.delivery, intent: state.intent, tierPct: d.pct,
          logo: state.logo ? state.logo.name : '',
          totalIncGst: Math.round(d.total * 100) / 100,
          depositIncGst: Math.round(d.deposit * 100) / 100,
          savingIncGst: Math.round(d.saving * 100) / 100,
          ref: ref,
          source: 'gift box builder v2'
        })
      };

      Object.keys(values).forEach(function (k) {
        var input = el('[data-gb-hidden="' + k + '"]');
        if (input) input.value = values[k];
      });

      // The customer's own confirmation. Shopify's contact form emails the
      // store and nothing else, so without this a buyer commits to four
      // figures and receives not a word. Fired as a Klaviyo client event; the
      // flow on that metric sends the email.
      //
      // keepalive matters: the native POST navigates away within milliseconds
      // of this line, and a normal fetch would be cancelled in flight. It also
      // means we never wait on Klaviyo — the order submits at the same speed
      // whether or not this succeeds, and a Klaviyo outage cannot block a sale.
      if (CFG.klaviyoCompany) {
        try {
          var kPhone = auMobile((el('#GiftBuilder-phone') || {}).value);
          var kName = ((el('#GiftBuilder-name') || {}).value || '').trim();
          var kProfile = { email: ((el('#GiftBuilder-email') || {}).value || '').trim() };
          if (kPhone) kProfile.phone_number = kPhone;
          if (kName) {
            var kParts = kName.split(/\s+/);
            kProfile.first_name = kParts[0];
            if (kParts.length > 1) kProfile.last_name = kParts.slice(1).join(' ');
          }
          var kOrg = ((el('#GiftBuilder-company') || {}).value || '').trim();
          if (kOrg) kProfile.organization = kOrg;

          fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(CFG.klaviyoCompany), {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
            body: JSON.stringify({ data: { type: 'event', attributes: {
              metric: { data: { type: 'metric', attributes: { name: 'Gift Box Order' } } },
              profile: { data: { type: 'profile', attributes: kProfile } },
              properties: {
                Reference: ref,
                Intent: isQuote ? 'quote' : 'order',
                Boxes: d.qty,
                Box: d.boxName,
                Glasses: values.glasses,
                Extras: values.addons,
                Delivery: values.delivery,
                PricePerBox: money(d.unit),
                OrderTotal: money(d.total),
                Deposit: isQuote ? '' : money(d.deposit),
                Balance: isQuote ? '' : money(balance),
                Saving: d.saving > 0 ? money(d.saving) : '',
                SavingPct: d.pct,
                DepositPct: CFG.depositPct,
                DepositDays: CFG.depositDays,
                OrderBy: CFG.orderBy,
                ReceiveBy: CFG.receiveBy,
                Company: kOrg,
                StillNeeded: values.needed,
                Logo: state.logo ? state.logo.name : ''
              }
            } } })
          }).catch(function () { /* never block the order on the email */ });
        } catch (e) { /* same */ }
      }

      // Normalise the mobile in place rather than mirroring it into a hidden
      // field: contact[Mobile] is the visible input, so it is sent even if this
      // line never runs. A number that is not a recognisable AU mobile is left
      // exactly as typed — better an odd format than a dropped contact.
      var phoneEl = el('#GiftBuilder-phone');
      if (phoneEl) {
        var e164 = auMobile(phoneEl.value);
        if (e164) phoneEl.value = e164;
      }

      // Same idea on the contact-form path, except nothing here can wait: the
      // browser is about to navigate away. keepalive is inside sendLogo's
      // fetch options only when the endpoint is set, so this is a no-op for
      // the default setup.
      if (CFG.logoUploadEndpoint && logoFile) sendLogo(ref);

      writeSnapshot(buildSnapshot(d));
      // Deliberately NOT clearing the saved configuration here: if Shopify
      // rejects the post, the page comes back without contact_posted and the
      // customer would otherwise return to an empty builder having lost
      // everything they configured. It is cleared on confirmed success below.

      // Let the native submit start first, then lock the button.
      submitting = true;
      var restoreLabel = (el('[data-gb-submit]') || {}).textContent || 'Order';
      setTimeout(function () {
        var btn = el('[data-gb-submit]');
        if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      }, 0);

      // This path hands the POST to Shopify and expects the browser to leave
      // the page, so nothing here ever ran on failure — if the submit did not
      // go, the button sat on 'Sending…' forever, disabled, with `submitting`
      // still true so a retry was impossible. The order was simply lost, with
      // nothing on screen to say so.
      //
      // It is not hypothetical: Shopify wraps this form in an invisible
      // hCaptcha that intercepts the submit, and when it cannot get a token
      // it fails without navigating ('form submit failed network-error').
      // A dropped connection mid-POST does the same thing.
      //
      // So: if we are still here after 15s, the submit did not happen. Give
      // the button back and say so. pagehide clears this on a real navigation,
      // so a slow-but-successful POST never trips it.
      clearTimeout(submitWatchdog);
      submitWatchdog = setTimeout(function () {
        submitting = false;
        var btn = el('[data-gb-submit]');
        if (btn) { btn.disabled = false; btn.textContent = restoreLabel; }
        var hint = el('[data-gb-submithint]');
        if (hint) {
          hint.hidden = false;
          hint.textContent = 'That did not send. Check your connection and try again, '
            + 'or email drink@grada.au and we will pick it up from there.';
        }
      }, 15000);
    });
  }

  /* ============================================================
     HERO FLIPBOOK — rapid flicks that decelerate into a calm loop
     ============================================================ */

  function startFlipbook() {
    if (reducedMotion()) return;
    var frames = root.querySelectorAll('[data-gb-flipbook] .gb-intro__frameimg');
    if (frames.length < 2) return;

    // The original flick, restored at Roscoe's call (30 Aug 2026). An audit
    // measured the 110ms opening at ~7 opposing luminance changes a second
    // across a full-bleed photo and flagged WCAG 2.3.1; the flashing area
    // measured ~21%, under the 25% general-flash threshold, and anyone with
    // prefers-reduced-motion never sees it at all (startFlipbook returns
    // early). Business decision, made with that trade-off understood — do not
    // slow this again without asking.
    var cadence = [110, 110, 110, 110, 110, 130, 150, 180, 220, 270, 330, 410, 510, 640, 800, 1000, 1300, 1700];
    var i = 0;
    var current = 0;
    var timer;

    function tick() {
      if (state.view === 'intro') {
        frames[current].classList.remove('is-on');
        current = (current + 1) % frames.length;
        frames[current].classList.add('is-on');
      }
      var delay = i < cadence.length ? cadence[i++] : 2400;
      timer = setTimeout(tick, delay);
    }

    timer = setTimeout(tick, 500);
    window.addEventListener('pagehide', function () { clearTimeout(timer); });
  }

  /* ============================================================
     POUR, SHAKE, SERVE: the build step's photo column (desktop)
     ============================================================ */

  // Three photos and a line each, cross-fading on progress bars the way
  // Apple's product highlights run: a bar fills, the next step lands. Click a
  // bar to jump, pause to stop. Starts paused for anyone who has asked for
  // less motion. CSS hides the column below 1024px, where nothing runs.
  function startServe() {
    var el = root.querySelector('[data-gb-serve]');
    if (!el) return;
    var frames = el.querySelectorAll('.gb-serve__frame');
    var steps = el.querySelectorAll('.gb-serve__step');
    var segs = el.querySelectorAll('.gb-serve__seg');
    var play = el.querySelector('.gb-serve__play');
    if (!frames.length || frames.length !== steps.length || frames.length !== segs.length) return;
    var i = 0;

    function show(n) {
      i = (n + frames.length) % frames.length;
      for (var k = 0; k < frames.length; k++) {
        frames[k].classList.toggle('is-on', k === i);
        steps[k].classList.toggle('is-was', steps[k].classList.contains('is-on') && k !== i);
        steps[k].classList.toggle('is-on', k === i);
        steps[k].setAttribute('aria-hidden', k === i ? 'false' : 'true');
        segs[k].classList.toggle('is-done', k < i);
        segs[k].classList.remove('is-on');
        if (k === i) segs[k].setAttribute('aria-current', 'step');
        else segs[k].removeAttribute('aria-current');
      }
      void segs[i].offsetWidth; // restart the fill animation
      segs[i].classList.add('is-on');
    }

    Array.prototype.forEach.call(segs, function (seg, k) {
      seg.addEventListener('click', function () { show(k); });
      seg.querySelector('i').addEventListener('animationend', function () {
        if (k === i) show(i + 1);
      });
    });

    function setPaused(p) {
      el.classList.toggle('is-paused', p);
      if (play) play.setAttribute('aria-label', p ? 'Play' : 'Pause');
    }
    if (play) play.addEventListener('click', function () { setPaused(!el.classList.contains('is-paused')); });
    if (reducedMotion()) setPaused(true);
    show(0);
  }

  /* ============================================================
     ROUTING + BOOT
     ============================================================ */

  window.addEventListener('popstate', function () {
    var h = (location.hash || '').replace('#', '');
    state.view = h === 'build' || h === 'send' || h === 'done' ? h : 'intro';
    render();
    window.scrollTo({ top: 0, behavior: 'auto' });
  });

  // Shopify redirects to ?contact_posted=true after a successful contact form
  // POST — the only success signal available on page load.
  var bootHash = (location.hash || '').replace('#', '');
  // Shopify re-renders the page with form.errors when it rejects a post. That
  // markup lives inside the Send view, which boots hidden — so without this the
  // customer sees the intro, no error, and no idea the order failed.
  var hasFormError = !!el('.gb-form__status--error');

  if (/[?&]contact_posted=true/.test(location.search)) {
    state.view = 'done';
    clearPersisted();
    // Drop the flag so a refresh or a shared URL cannot replay "Order received".
    try {
      var clean = location.pathname + location.search.replace(/([?&])contact_posted=true&?/, '$1').replace(/[?&]$/, '');
      history.replaceState(null, '', clean + '#done');
    } catch (e) {}
  } else if (hasFormError) {
    state.view = 'send';
    restorePersisted();
  } else if (bootHash === 'done' && readSnapshot()) {
    // A Laurie order was placed; reloading must not strand the customer away
    // from their invoice link, which lives only in the snapshot.
    state.view = 'done';
  } else {
    if (bootHash === 'build' || bootHash === 'send') state.view = bootHash;
    clearSnapshot();
    // Pick up where they left off — a configured box survives leaving the page.
    restorePersisted();
  }

  syncHeaderOffset();
  window.addEventListener('resize', syncHeaderOffset);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncHeaderOffset);

  render();
  startFlipbook();
  startServe();
})();
